import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, groomingPdfUrl, formatErr } from "@/lib/api";
import { GROOMING_CATALOG, GROOMING_CONDITIONS, money, ageFromDob, ageBand, ageBandLabel } from "@/lib/brands";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, ArrowRight, Save, Scissors, Download, CheckCircle2, ClipboardList,
  Truck, Stethoscope, Sparkles, PawPrint,
} from "lucide-react";
import { toast } from "sonner";
import { CloseFlow } from "@/components/CloseFlow";

const blank = { services: {}, logistics: {}, condition_check: {}, notes: "" };

const STEPS = [
  { key: "services", label: "Services", icon: Scissors, hint: "Pick the grooming services" },
  { key: "logistics", label: "Logistics", icon: Truck, hint: "Pickup & drop-off" },
  { key: "condition", label: "Intake Check", icon: Stethoscope, hint: "Dog condition on arrival" },
  { key: "review", label: "Review", icon: ClipboardList, hint: "Confirm & save" },
];

export default function GroomingForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const patientId = sp.get("patient");
  const [gid, setGid] = useState(id || null);
  const [patient, setPatient] = useState(null);
  const [rec, setRec] = useState(blank);
  const [status, setStatus] = useState("open");
  const [amountPaid, setAmountPaid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState(0);

  useEffect(() => {
    (async () => {
      if (id) {
        const { data } = await api.get(`/grooming/${id}`);
        setRec({ services: data.services || {}, logistics: data.logistics || {}, condition_check: data.condition_check || {}, notes: data.notes || "" });
        setStatus(data.status); setAmountPaid(data.amount_paid);
        const { data: p } = await api.get(`/patients/${data.patient_id}`);
        setPatient(p);
      } else if (patientId) {
        const { data: p } = await api.get(`/patients/${patientId}`);
        setPatient(p);
      }
    })();
  }, [id, patientId]);

  const setServices = (fn) => setRec((r) => ({ ...r, services: fn(r.services) }));
  const toggleMulti = (k, opt) => setServices((s) => ({ ...s, [k]: { ...s[k], options: { ...(s[k]?.options || {}), [opt]: !(s[k]?.options?.[opt]) } } }));
  const toggleSingle = (k) => setServices((s) => ({ ...s, [k]: { checked: !(s[k]?.checked) } }));
  const setDesc = (k, opt, val) => setServices((s) => ({ ...s, [k]: { ...s[k], descriptions: { ...(s[k]?.descriptions || {}), [opt]: val } } }));
  const setSeverity = (k, val) => setServices((s) => ({ ...s, [k]: { ...s[k], severity: val } }));
  const setLog = (key, patch) => setRec((r) => ({ ...r, logistics: { ...r.logistics, [key]: { ...(r.logistics[key] || {}), ...patch } } }));
  const setCond = (key, patch) => setRec((r) => ({ ...r, condition_check: { ...r.condition_check, [key]: { ...(r.condition_check[key] || {}), ...patch } } }));

  const [openSections, setOpenSections] = useState({});
  const sectionActive = (cat) => {
    const sel = rec.services[cat.key] || {};
    if (cat.type === "single") return !!sel.checked;
    return !!openSections[cat.key] || Object.values(sel.options || {}).some(Boolean);
  };
  const toggleSection = (cat) => {
    if (cat.type === "single") { toggleSingle(cat.key); return; }
    if (sectionActive(cat)) {
      setServices((s) => ({ ...s, [cat.key]: {} }));
      setOpenSections((o) => ({ ...o, [cat.key]: false }));
    } else {
      setOpenSections((o) => ({ ...o, [cat.key]: true }));
    }
  };

  // ---- live summary ----
  const summary = useMemo(() => {
    const out = [];
    for (const cat of GROOMING_CATALOG) {
      const sel = rec.services[cat.key] || {};
      if (cat.type === "single") {
        if (sel.checked) out.push({ cat: cat.label, items: ["Included"] });
      } else {
        const items = Object.entries(sel.options || {}).filter(([, v]) => v).map(([o]) => {
          if (cat.type === "dematting" && o === "Mats" && sel.severity) return `${o} (${sel.severity})`;
          return o;
        });
        if (items.length) out.push({ cat: cat.label, items });
      }
    }
    return out;
  }, [rec.services]);

  const totalServices = summary.reduce((n, s) => n + s.items.length, 0);
  const logisticsSel = ["pickup", "dropoff"].filter((k) => rec.logistics[k]?.checked);
  const conditionSel = GROOMING_CONDITIONS.filter((c) => rec.condition_check[c]?.checked);

  const save = async () => {
    if (!patient) return;
    setBusy(true);
    try {
      if (gid) {
        await api.put(`/grooming/${gid}`, { patient_id: patient.id, ...rec });
        toast.success("Grooming saved");
      } else {
        const { data } = await api.post("/grooming", { patient_id: patient.id, ...rec });
        setGid(data.id);
        toast.success("Grooming booking created");
        navigate(`/grooming/${data.id}/edit`, { replace: true });
      }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openClose = async () => {
    let amt = "";
    if (gid) {
      try {
        const { data } = await api.get(`/grooming/${gid}/suggested-amount`);
        if (data.suggested) amt = data.suggested;
      } catch (e) { console.error("No suggested grooming price available:", e); }
    }
    setAmount(amt);
    setCloseOpen(true);
  };

  const confirmClose = async (amt) => {
    const { data } = await api.post(`/grooming/${gid}/close`, { amount: amt });
    setStatus("closed"); setAmountPaid(data.amount_paid);
    return gid;
  };

  const scheduleNext = async (dateISO) => {
    await api.post("/service-reminders", { record_type: "grooming", record_id: gid, due_date: dateISO });
  };

  if (!patient) return <div className="p-8">Loading…</div>;

  const band = ageBand(patient.dob);
  const stepKey = STEPS[step].key;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="p-4 md:p-8 max-w-[1180px] mx-auto animate-fade-in">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>

      {/* Hero */}
      <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-primary/10 via-card to-accent/10 p-6 md:p-7 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center shrink-0"><Scissors className="h-6 w-6 text-primary" /></div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-primary text-3xl font-bold tracking-tight">Grooming Booking</h1>
                <span data-testid="grooming-status" className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${status === "closed" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>{status === "closed" ? "Closed" : "Open"}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                <PawPrint className="h-3.5 w-3.5" /> {patient.pet_name} · {patient.owner_name} · {patient.case_number}
                <span className="px-1.5 py-0.5 rounded bg-accent/15 text-accent-foreground/80 text-xs">{ageBandLabel[band]} · {ageFromDob(patient.dob) || "age n/a"}</span>
                <span>· {patient.owning_branch}</span>
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy} variant="outline" data-testid="save-grooming-btn"><Save className="h-4 w-4 mr-2" />Save</Button>
            {gid && <a href={groomingPdfUrl(gid)} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="grooming-pdf-btn"><Download className="h-4 w-4 mr-2" />PDF</Button></a>}
            <Button onClick={openClose} disabled={busy || !gid} data-testid="close-grooming-btn"><CheckCircle2 className="h-4 w-4 mr-2" />{status === "closed" ? "Edit Amount" : "Close Record"}</Button>
          </div>
        </div>

        {/* Stepper */}
        <div className="mt-6 flex items-center">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.key} className="flex items-center flex-1 last:flex-none">
                <button type="button" onClick={() => setStep(i)} data-testid={`grooming-step-${s.key}`}
                  className="flex items-center gap-2.5 group">
                  <span className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-all ${active ? "bg-primary border-primary text-primary-foreground shadow-md scale-105" : done ? "bg-primary/15 border-primary/40 text-primary" : "bg-card border-border text-muted-foreground group-hover:border-primary/40"}`}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </span>
                  <span className={`hidden sm:block text-sm font-medium transition-colors ${active ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${i < step ? "bg-primary/40" : "bg-border"}`} />}
              </div>
            );
          })}
        </div>
      </div>

      {status === "closed" && <Card className="p-4 mb-4 border-emerald-500/30 bg-emerald-500/5 text-sm">Amount paid: <span className="font-semibold">{money(amountPaid)}</span> — services remain editable; regenerate the PDF anytime.</Card>}

      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        {/* ---- left: current step ---- */}
        <div className="space-y-4 animate-fade-in" key={stepKey}>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
            <span className="text-muted-foreground/60">·</span>
            <p className="text-sm text-muted-foreground">{STEPS[step].hint}</p>
          </div>

          {stepKey === "services" && (
            <div className="grid sm:grid-cols-2 gap-4">
              {GROOMING_CATALOG.map((cat) => {
                const sel = rec.services[cat.key] || {};
                const active = sectionActive(cat);
                return (
                  <Card key={cat.key} data-testid={`grooming-card-${cat.key}`} className={`p-4 border transition-all ${active ? "border-primary/50 bg-primary/5 shadow-sm" : "border-border/60 hover:border-primary/30"}`}>
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <Checkbox checked={active} onCheckedChange={() => toggleSection(cat)} data-testid={`grooming-section-${cat.key}`} />
                      <span className="font-primary font-semibold flex items-center gap-2"><Sparkles className={`h-3.5 w-3.5 ${active ? "text-primary" : "text-muted-foreground/50"}`} />{cat.label}</span>
                    </label>
                    {cat.type !== "single" && active && (
                      <div className="mt-3 pl-7 space-y-2.5 animate-fade-in">
                        {cat.options.map((opt) => {
                          const checked = !!sel.options?.[opt];
                          return (
                            <div key={opt} className="space-y-2">
                              <label className="flex items-center gap-2.5 cursor-pointer text-sm">
                                <Checkbox checked={checked} onCheckedChange={() => toggleMulti(cat.key, opt)} data-testid={`grooming-${cat.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`} />
                                <span>{opt}</span>
                              </label>
                              {cat.type === "desc" && checked && (
                                <Textarea rows={2} placeholder={`${opt} details…`} value={sel.descriptions?.[opt] || ""} onChange={(e) => setDesc(cat.key, opt, e.target.value)} />
                              )}
                              {cat.type === "dematting" && opt === "Mats" && checked && (
                                <div>
                                  <Label className="text-xs text-muted-foreground">Severity</Label>
                                  <RadioGroup value={sel.severity || ""} onValueChange={(v) => setSeverity(cat.key, v)} className="flex gap-4 mt-1">
                                    {["Low", "Medium", "High"].map((sv) => (
                                      <div key={sv} className="flex items-center gap-2"><RadioGroupItem value={sv} id={`mat-${sv}`} data-testid={`mats-severity-${sv.toLowerCase()}`} /><Label htmlFor={`mat-${sv}`} className="cursor-pointer text-sm">{sv}</Label></div>
                                    ))}
                                  </RadioGroup>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {stepKey === "logistics" && (
            <Card className="p-5 border-border/60">
              <h3 className="font-primary font-semibold mb-4 flex items-center gap-2"><Truck className="h-4 w-4 text-primary" />Pickup & Drop-off</h3>
              {["pickup", "dropoff"].map((k) => (
                <div key={k} className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox checked={!!rec.logistics[k]?.checked} onCheckedChange={(v) => setLog(k, { checked: !!v })} data-testid={`logistics-${k}`} />
                    <span className="capitalize font-medium">{k === "dropoff" ? "Drop-off" : "Pickup"}</span>
                  </label>
                  {rec.logistics[k]?.checked && <Input className="ml-7 mt-2" placeholder="Address / time notes" value={rec.logistics[k]?.notes || ""} onChange={(e) => setLog(k, { notes: e.target.value })} />}
                </div>
              ))}
            </Card>
          )}

          {stepKey === "condition" && (
            <Card className="p-5 border-border/60">
              <h3 className="font-primary font-semibold mb-4 flex items-center gap-2"><Stethoscope className="h-4 w-4 text-primary" />Dog Condition Quick Check (intake)</h3>
              {GROOMING_CONDITIONS.map((c) => (
                <div key={c} className="mb-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox checked={!!rec.condition_check[c]?.checked} onCheckedChange={(v) => setCond(c, { checked: !!v })} data-testid={`condition-${c.replace(/\s+/g, "-").toLowerCase()}`} />
                    <span>{c}</span>
                  </label>
                  {rec.condition_check[c]?.checked && <Input className="ml-7 mt-2" placeholder={`${c} notes`} value={rec.condition_check[c]?.desc || ""} onChange={(e) => setCond(c, { desc: e.target.value })} />}
                </div>
              ))}
              <div className="mt-4">
                <Label>General notes</Label>
                <Textarea rows={2} value={rec.condition_check.notes || ""} onChange={(e) => setRec((r) => ({ ...r, condition_check: { ...r.condition_check, notes: e.target.value } }))} data-testid="condition-notes" />
              </div>
            </Card>
          )}

          {stepKey === "review" && (
            <div className="space-y-4">
              <Card className="p-5 border-border/60">
                <h3 className="font-primary font-semibold mb-3 flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" />Booking notes</h3>
                <Textarea rows={3} placeholder="Any special instructions for this booking…" value={rec.notes} onChange={(e) => setRec((r) => ({ ...r, notes: e.target.value }))} data-testid="grooming-notes" />
              </Card>
              <Card className="p-5 border-border/60 bg-muted/20">
                <h3 className="font-primary font-semibold mb-3">Ready to book</h3>
                <p className="text-sm text-muted-foreground">Review the summary on the right, then <b>Save</b> the booking. When the groom is done, use <b>Close Record</b> to set the final amount, then choose to download the PDF and schedule the next session.</p>
                <div className="flex gap-2 mt-4">
                  <Button onClick={save} disabled={busy} data-testid="review-save-btn"><Save className="h-4 w-4 mr-2" />{gid ? "Save changes" : "Create booking"}</Button>
                  <Button onClick={openClose} disabled={busy || !gid} variant="outline" data-testid="review-close-btn"><CheckCircle2 className="h-4 w-4 mr-2" />Close Record</Button>
                </div>
              </Card>
            </div>
          )}

          {/* nav */}
          <div className="flex justify-between pt-2">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="grooming-prev-btn"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
            {!isLast && <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} data-testid="grooming-next-btn">Continue<ArrowRight className="h-4 w-4 ml-2" /></Button>}
          </div>
        </div>

        {/* ---- right: sticky live summary ---- */}
        <Card className="p-5 border-border/60 lg:sticky lg:top-6" data-testid="grooming-summary">
          <h3 className="font-primary text-lg font-semibold mb-1">Booking Summary</h3>
          <p className="text-xs text-muted-foreground mb-4">{patient.pet_name} · {ageBandLabel[band]}</p>

          <div className="space-y-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Services ({totalServices})</p>
              {summary.length === 0 ? <p className="text-muted-foreground italic">No services selected yet.</p> : (
                <div className="space-y-2">
                  {summary.map((g) => (
                    <div key={g.cat}>
                      <p className="font-medium text-xs text-primary">{g.cat}</p>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {g.items.map((it) => <span key={it} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-foreground/80">{it}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {logisticsSel.length > 0 && (
              <div className="border-t border-border/60 pt-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Logistics</p>
                <div className="flex flex-wrap gap-1">
                  {logisticsSel.map((k) => <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent-foreground/80 capitalize">{k === "dropoff" ? "Drop-off" : "Pickup"}</span>)}
                </div>
              </div>
            )}

            {conditionSel.length > 0 && (
              <div className="border-t border-border/60 pt-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Intake flags</p>
                <div className="flex flex-wrap gap-1">
                  {conditionSel.map((c) => <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700">{c}</span>)}
                </div>
              </div>
            )}

            {status === "closed" && (
              <div className="border-t border-border/60 pt-3 flex items-center justify-between">
                <span className="text-muted-foreground">Amount paid</span>
                <span className="font-semibold">{money(amountPaid)}</span>
              </div>
            )}
          </div>

          <Button onClick={save} disabled={busy} className="w-full mt-5" data-testid="summary-save-btn"><Save className="h-4 w-4 mr-2" />{gid ? "Save booking" : "Create booking"}</Button>
        </Card>
      </div>

      <CloseFlow
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={status === "closed" ? "Update grooming amount" : "Close grooming"}
        suggested={amount}
        initialAmount={amountPaid ?? ""}
        onConfirmClose={confirmClose}
        pdfUrl={groomingPdfUrl}
        onSchedule={scheduleNext}
        scheduleLabel="next grooming session"
      />
    </div>
  );
}
